import { Component, OnInit, Input, Optional, Inject } from '@angular/core';
import { ContentStudioService } from './../../services/content-studio.service';
import { PershingTranslationService } from '@pershing/pershing-core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'image-content',
  templateUrl: './image-content.component.html'
})
export class ImageContentComponent implements OnInit {
  @Input() contentName: string; // aka file name, internal name, article id. ex: ABOUT_US
  themeType: string = 'conservative';
  imageSrc: string = '';
  imageFormat: string = 'data:image/png;base64, ';
  unsubscribe: Subject<void> = new Subject<void>();
  defaultLang: string = 'en';
  isStaging: boolean = true;  // Whether image is fetched from staging or publish.
  siteDomain: string = '';  // To derive content path;
  imageClass: string = '';
  hierarchy: string; // should be comma separated string
  isSitebuilderCMSEnabled: boolean = false;
  // constants
  private DESKTOP: string = 'desktop';
  private DESKTOP_CONTENT_NAME: string = 'carousel-desktop.png';
  private DESKTOP_IMAGE_CLASS: string = 'ws-Image';
  private TABLET: string = 'tablet';
  private TABLET_CONTENT_NAME: string = 'carousel-tablet.png';
  private TABLET_IMAGE_CLASS: string = 'ws-Image-tablet';
  private MOBILE: string = 'mobile';
  private MOBILE_CONTENT_NAME: string = 'carousel-mobile.png';
  private MOBILE_IMAGE_CLASS: string = 'ws-Image-mobile';

  constructor(
    private contentservice: ContentStudioService,
    private translationService: PershingTranslationService,
    @Optional() @Inject('AppSettings') private appSettings: any) { }

  ngOnInit() {
    // The domain will either be the targeted preview domain (e.g. used in Site Builder tool context) or itself.
    this.siteDomain = !!this.appSettings.previewSite ? this.appSettings.previewSite : this.appSettings.ibdConfigMap.URL;
    // Always use staging for preview otherwise, it should depends on the site itself.
    this.isStaging = !!this.appSettings.previewSite ? true : this.appSettings.ibdConfigMap.staging;
    this.isSitebuilderCMSEnabled = this.appSettings.isSitebuilderCMSEnabled;
    this.initFullImageSrcUrl();
  }

  initFullImageSrcUrl() {
    // e.g. https://staging-previewx.test.netxinvestor.com/test-netxinvestor-webcontent-svc-uat-app/webcontent/imageContentDirect/
    //      sitebuilder-site/sitebuilder-site?contentName=carousel.png&isStaging=true&isComingFromCreatedSite=true&
    //      contentPath=retail/previewx-test-netxinvestor-com/conservative/en
    if (this.appSettings.deviceType === this.DESKTOP) {
      this.contentName = this.DESKTOP_CONTENT_NAME;
      this.imageClass = this.DESKTOP_IMAGE_CLASS;
    } else if (this.appSettings.deviceType === this.TABLET) {
      this.contentName = this.TABLET_CONTENT_NAME;
      this.imageClass = this.TABLET_IMAGE_CLASS;
    } else if (this.appSettings.deviceType === this.MOBILE) {
      this.contentName = this.MOBILE_CONTENT_NAME;
      this.imageClass = this.MOBILE_IMAGE_CLASS;
    }

    if (this.isSitebuilderCMSEnabled) {
      this.initTheHierarchySoItFallBackToPreviewSite();
      this.imageSrc = this.contentservice.baseUrl + '/imageContentDirect/' + this.appSettings.cms.siteId + '/' + this.appSettings.cms.stagingSiteId +
        '?contentName=' + this.contentName + '&isStaging=' + this.isStaging + '&isComingFromCreatedSite=' + this.appSettings.isSitebuilder +
        '&contentPath=' + this.getResolvedContentPath(this.siteDomain) + '&hierarchy=' + this.hierarchy;
    } else {
      this.imageSrc = this.appSettings.siteCdnUrl + '/images/' + this.contentName;
    }
  }

  /**
   * Set the preview site to be top of the hierarchy. So it fallback to getting image from preview site if it can't get any for the site that it want to preview 
   */
  initTheHierarchySoItFallBackToPreviewSite() {
    let urlOfSiteThatWeUseForPreview = !!this.appSettings.previewSite ? this.appSettings.ibdConfigMap.URL : '';
    this.hierarchy = this.getResolvedContentPath(urlOfSiteThatWeUseForPreview);
  }

  getImageContent() {
    this.contentservice.getImageContent(this.appSettings.cms.siteId, this.appSettings.cms.stagingSiteId, this.contentName, this.getResolvedContentPath(this.siteDomain), [],
      this.appSettings.ibdConfigMap.staging, this.appSettings.isSitebuilder).pipe(takeUntil(this.unsubscribe)).subscribe(rsp => {
        if (rsp && rsp.errorCode === 0 && !!rsp.response) {
          this.imageSrc = this.imageFormat + rsp.response;
        }
      });
  }

  getResolvedContentPath(siteUrl) {
    // Path for Crafter consists of platform + resolved site domain (sub domain & domain) + theme + language.
    // e.g. for prodution site:  https://pncinvestments.netxinvestor.com/ 
    //    Domain:  pncinvestments.netxinvestor.com
    //    Resolved prod content path:  retail/pncinvestments-netxinvestor-com/conservative/en
    //    Resolved staging content path:  retail/pncinvestments-qa-netxinvestor-com/conservative/en
    // Also currently we use one image for all languages - should change needed, replace it with this.translationService.userLang.
    if (!siteUrl) return '';
    return this.appSettings.platform + '/' + this.domainToPath(siteUrl) + '/' + this.themeType + '/' + this.defaultLang;
  }

  domainToPath(fullDomain: string) {
    let path = '';
    if (!!fullDomain) {
      // Remove prefix 'staging-' and convert all dot to dash.
      let domain = fullDomain.indexOf('?') > 0 ? fullDomain.slice(0, fullDomain.indexOf('?')) : fullDomain;
      path = domain.replace('staging-', '').replace(/\./g, '-');
    }
    return path;
  }

}
